import React, { FC, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useSwipeable } from 'react-swipeable';

import { CompleteBongQuery_venue_company_categories_items as Item } from '~Scenes/POS/Queries/__queries__';
import {
  Badge,
  BlueButton,
  Box,
  Button,
  Card,
  Column,
  Columns,
  Dimmer,
  GrayButton,
} from '~Styles';
import { useButton, useCurrency, useDeviceInfo, useI18n, useLogger, useSelector } from '~Hooks';
import { MODIFICATION_GROUP_TYPE, VARIATION_TYPE } from '../../../../../__queries__';
import CheckIcon from '~Assets/icons/check.svg';
import { LocalVariation } from '~Types';
import Icon from '~ComponentsImproved/Icon';
import { SelectedUpsales } from '~Scenes/POS/Components/ItemModifications';
import { useEntryFinder } from '~Components/EntryFinder';
import useItemModifications from '~Hooks/ItemModifications/useItemModifications';
import { sortUpsales } from '~Utils/sortUpsales';

const StyledColumns = styled(Columns)<{ hasNotch?: boolean }>`
  height: 100vh;
  overflow: hidden;
  background: var(--colorBlueGray1200);
  width: calc(100% + 8px);
  ${({ hasNotch }) => hasNotch && 'padding-top: 42px'};

  & > * {
    height: 100%;
    overflow: hidden;
  }
`;

const PriceBadge = styled(Badge)`
  font-size: 10px;
  margin-right: 4px;
  padding: 2px 4px;
`;

const CategoryList = styled(Box)`
  height: 100%;
`;

const Category = styled(Box)<{ selected?: boolean }>`
  font-size: 14px;
  ${({ selected }) =>
    selected &&
    css`
      box-shadow: 4px 0px 0px 0px var(--colorUIOrange) inset; // Acts like a border left, avoids content shifting
      font-weight: 600;
    `};
`;

const OptionList = styled(Box)`
  height: 100%;
  overflow: auto;
  padding-top: 8px;
  font-size: 14px;
  padding-bottom: 500px; // This gives the list some room at the bottom
  &::-webkit-scrollbar {
    display: none;
  }
  & > div:not(:last-child) {
    margin-bottom: 12px;
  }
`;

const GroupWrapper = styled(Box)`
  position: relative;
  padding-top: 8px;

  & > div:not(:last-child) {
    margin-bottom: 6px;
  }
`;

const SelectionIndicator = styled.div<{
  selected: boolean;
  type: VARIATION_TYPE | MODIFICATION_GROUP_TYPE;
}>`
  height: 16px;
  width: 16px;
  border: 1px solid;
  border-color: ${({ selected }) =>
    selected ? 'var(--colorUIGreen)' : 'var(--colorBlueGray700)'};
  background-color: ${({ selected }) => (selected ? 'var(--colorUIGreen)' : 'transparent')};
  border-radius: ${({ type }) => (type === 'SINGLE' ? '50%' : '4px')};
  display: flex;
`;

const Option = styled(Card)<{
  selected: boolean;
  outOfStock: boolean;
}>`
  ${({ outOfStock }) =>
    outOfStock
      ? css`
          background: var(--colorBlueGray1200);
          color: var(--colorBlueGray500);
        `
      : css`
          background: var(--colorBlueGray1100);
          color: var(--colorBlueGray100);
        `}
  padding: 14px 8px 14px 12px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border: 1px solid #3b3b3b;
`;

const GroupTitle = styled.div<{ required: boolean }>`
  color: var(--colorBlueGray500);
  ${({ required }) =>
    required &&
    css`
      &:after {
        content: '*';
        color: var(--colorStatusRed);
        margin-left: 4px;
      }
    `}
`;

interface Props {
  item: Item;
  onCancel: () => void;
  onComplete: (
    variations: LocalVariation[],
    removedVariations: LocalVariation[],
    upsaleItems: SelectedUpsales,
  ) => void;
}

/** Handles the ItemModification selection for handheld */
const ItemModifications: FC<Props> = ({ item, onCancel, onComplete }) => {
  const findEntryFromItem = useEntryFinder();

  const currency = useCurrency();
  const deviceInfo = useDeviceInfo();
  const logger = useLogger('variations/upsells');

  const [touching, setTouching] = useState<boolean>(false);
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const [selectedUpsales, setSelectedUpsales] = useState<SelectedUpsales>([]);

  const filteredUpsales = sortUpsales(
    item.upsales.filter((u) => !u.hideInPOS),
    item.upsalesSortOrder ?? [],
  );

  const itemModifications = useItemModifications(item.modificationGroups ?? []);

  const { i18n } = useI18n();

  // Holds a reference to all variation group DOM containers, to check which is active on screen
  const observer = useRef<NodeListOf<Element> | null>(null);

  // Effect to attach event listeners for the group scrolling logic
  useEffect(() => {
    const groupElems = document.querySelectorAll('[data-group]');
    observer.current = groupElems;

    const listElement = document.getElementById('variation-list');
    if (!listElement || !itemModifications.modificationGroups.length) {
      return;
    }

    // Set 0th group as active by default
    setActiveGroup(itemModifications.modificationGroups[0].id);

    // Check what group DOM element should be considered "active"
    const checkActiveGroup = () => {
      if (!observer.current) {
        return;
      }
      // Get all elements that are within -10 to 200 px into the screen
      const contenders = Array.from(observer.current).filter(
        (element) =>
          element.getBoundingClientRect().top < 200 && element.getBoundingClientRect().top > -10,
      );

      // Set last contender as the active one (the latest to fill criteria)
      if (contenders.length) {
        setActiveGroup(contenders[contenders.length - 1].id);
      }
    };

    listElement.addEventListener('scroll', checkActiveGroup);
    return () => {
      listElement.removeEventListener('scroll', checkActiveGroup);
    };
  }, []);

  // Blocks variation select when swiping/scrolling
  const handlers = useSwipeable({
    delta: 10,
    onSwiping: () => setTouching(false),
  });

  const addButton = useButton({
    onClick: () => handleDone(),
  });

  const cancelButton = useButton({
    onClick: () => onCancel(),
  });

  // Check if all required modification groups have at least one selection
  const selectionValid = itemModifications.modificationGroups.every((group) => group.valid);

  const hasVariations =
    itemModifications.modificationGroups.flatMap((mg) => mg.variations).length > 0;
  const hasUpsales = item && Array.isArray(filteredUpsales) && !!filteredUpsales.length;

  /** The item doesnt have any groups with variations or upsells assigned to it - so then we can skip this whole modification selection */
  if (!hasVariations && !hasUpsales) {
    onComplete([], [], []);
    return <></>;
  }

  /** Render modification group with their options */
  const modificationGroupNodes = itemModifications.modificationGroups.map((group) => {
    return (
      <GroupWrapper key={group.id} data-group id={group.id} className="group">
        <GroupTitle required={group.required}>{group.title}</GroupTitle>
        {group.variations.map((variation) => (
          <Option
            key={variation.id}
            outOfStock={!!variation.isOutOfStock}
            selected={variation.selected}
            onTouchEnd={() => handleVariationSelect(variation as LocalVariation)}>
            <span>{variation.name}</span>
            <Box display="flex" flexDirection="row" alignItems="center">
              {variation.price > 0 && (
                <PriceBadge backgroundColor="colorBlack">
                  {currency.format(variation.price)}
                </PriceBadge>
              )}
              <SelectionIndicator type={group.type} selected={variation.selected}>
                {variation.selected && <Icon src={CheckIcon} color="var(--colorWhite)" />}
              </SelectionIndicator>
            </Box>
          </Option>
        ))}
      </GroupWrapper>
    );
  });

  /** Render modification group with their options */
  const upsellGroupNodes = filteredUpsales.map((group) => {
    return (
      <GroupWrapper key={group.id} data-group id={group.id} className="group">
        <GroupTitle required={false}>{i18n(group.name)}</GroupTitle>
        {group.items.map((upsaleItem) => {
          const foundEntry = findEntryFromItem(upsaleItem.id);

          // Don't render items that is not included in menu
          if (!foundEntry) {
            // Likely a misconfiguration - An item has an upsell that is not included in the menu
            logger.warn(
              `Upsell in Handheld not rendered, could not find entry for ${upsaleItem.id}`,
              foundEntry,
            );
            return;
          }

          return (
            <Option
              key={upsaleItem.id}
              outOfStock={false}
              selected={
                selectedUpsales ? !!selectedUpsales.find((i) => i.id === upsaleItem.id) : false
              }
              onTouchEnd={() => onSelectUpsell(upsaleItem.id, i18n(upsaleItem.titleLang))}>
              <span>{i18n(upsaleItem.titleLang)}</span>
              <Box display="flex" flexDirection="row" alignItems="center">
                {foundEntry.price > 0 && (
                  <PriceBadge backgroundColor="colorBlack">
                    {currency.format(foundEntry.price)}
                  </PriceBadge>
                )}
                <SelectionIndicator
                  type={MODIFICATION_GROUP_TYPE.MULTIPLE}
                  selected={Boolean(selectedUpsales?.some((i) => i.id === upsaleItem.id))}>
                  {Boolean(selectedUpsales?.some((i) => i.id === upsaleItem.id)) && (
                    <Icon src={CheckIcon} color="var(--colorWhite)" />
                  )}
                </SelectionIndicator>
              </Box>
            </Option>
          );
        })}
      </GroupWrapper>
    );
  });

  // Fired when touching a variation option
  const handleVariationSelect = (variation: LocalVariation) => {
    if (!touching || variation.isOutOfStock) {
      return;
    }
    itemModifications.onSelectVariation(variation.id);
  };

  // Fired when touching a upsell option
  const onSelectUpsell = (itemId: string, itemTitle: string) => {
    if (!touching) {
      return;
    }

    if (selectedUpsales.some((i) => i.id === itemId)) {
      setSelectedUpsales(selectedUpsales.filter((i) => i.id !== itemId));
    } else {
      setSelectedUpsales([...selectedUpsales, { id: itemId, title: itemTitle }]);
    }
  };

  // Used to determine if the user is scrolling, or touching
  const handleTouchStart = (e: React.UIEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setTouching(true);
  };

  // Fired when clicking a category, will scroll that category into view
  const scrollToGroup = (groupId: string) => {
    if (typeof document !== 'undefined') {
      document.getElementById(groupId)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Called when the user taps "add", i.e when they are done with selection
  const handleDone = () => {
    onComplete(
      itemModifications.selectedVariations,
      itemModifications.removedVariations,
      selectedUpsales,
    );
  };

  return (
    <Dimmer zIndex={9} active>
      <StyledColumns space="small" hasNotch={deviceInfo?.hasNotch}>
        <Column
          width={5}
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          boxShadow="rgb(0 0 0 / 91%) 0px 0px 20px">
          <CategoryList>
            {itemModifications.modificationGroups.map((group) => (
              <Category
                padding="small"
                selected={activeGroup === group.id}
                onTouchEnd={() => scrollToGroup(group.id)}
                key={group.id}>
                {group.title}
              </Category>
            ))}
            {filteredUpsales.map((group) => (
              <Category
                padding="small"
                selected={activeGroup === group.id}
                onTouchEnd={() => scrollToGroup(group.id)}
                key={group.id}>
                {i18n(group.name)}
              </Category>
            ))}
          </CategoryList>
          <Box padding="xs" fluid>
            {selectionValid ? (
              <BlueButton fluid marginBottom="xs" {...addButton}>
                {i18n('General.Add')}
              </BlueButton>
            ) : (
              <GrayButton fluid marginBottom="xs" {...addButton}>
                {i18n('General.Add')}
              </GrayButton>
            )}
            <Button
              fluid
              background="none"
              color="colorBlueGray200"
              {...cancelButton}
              marginBottom="xs">
              {i18n('General.Cancel')}
            </Button>
          </Box>
        </Column>
        <Column boxShadow="-4px 0px 76px -10px rgba(0,0,0,0.1) inset">
          <OptionList
            padding="xs"
            {...handlers}
            onTouchStart={handleTouchStart}
            id="variation-list">
            {modificationGroupNodes}
            {upsellGroupNodes}
          </OptionList>
        </Column>
      </StyledColumns>
    </Dimmer>
  );
};

export default ItemModifications;
